import React, { useCallback, useEffect, useMemo, useState } from "react";

import type { CreatedClientSummary } from "@/modules/dashboard/clients/components/create/ClientSuccessStep";
import { ClientsService } from "@/modules/dashboard/clients/services/clientsService";
import type { ClientAccount } from "@/modules/dashboard/clients/types/clients-api.types";
import {
  clientDisplayName,
  normalizeClientListItem,
} from "@/modules/dashboard/clients/utils/normalizeClient";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { useAvailableCorridors } from "@/shared/hooks/useAvailableCorridors";
import useTranslations from "@/shared/lang/useTranslations";
import {
  formatPixInput,
  PIX_KEY_META,
  PIX_KEY_TYPE_BY_LEGACY,
  type PixKeyTypeEnum,
  pixKeyValid,
} from "@/shared/utils/pixKey";

import { resolveColRail } from "../../constants/dispersions.constants";
import { useBrebKeyResolution } from "../../hooks/useBrebKeyResolution";
import { useDispersions } from "../../hooks/useDispersions";
import {
  BrebKeyType,
  ColombianDocumentType,
  isBrebKeyValid,
  resolveCbuCvuKind,
  TransfiyaAccountType,
} from "../../services/paymentsPayout.service";
import { TransfiyaBanksService, type TransfiyaBank } from "../../services/transfiyaBanks.service";
import type { CountryCode } from "../../types/countryFields.types";
import type { RecipientMode } from "../../types/dispersions-api.types";
import { inferBrebKeyType } from "../../utils/brebKeyLookup";
import { colombiaRailUnavailableKey } from "../../utils/colombiaRailAvailability";
import { parseAmountInputDecimal } from "../../utils/currencyFormatter";
import { BrebRecipientConfirmation } from "./BrebRecipientConfirmation";
import { ClientKycUnavailableAlert } from "./ClientKycUnavailableAlert";
import { FieldInput, FieldSelect } from "./fields";
import { RecipientClientSelector } from "./RecipientClientSelector";
import { RecipientModeSwitch } from "./RecipientModeSwitch";
import { RegisterAccountAction } from "./RegisterAccountAction";
import { RegisterClientAction } from "./RegisterClientAction";

interface Step2CountryFieldsProps {
  destination: CountryCode;
  destinationCurrency: string;
  errors?: Record<string, string>;
  touched?: Record<string, boolean>;
  onBlur?: (field: string) => void;
}

// PIX meta/mask/validation moved to `@/shared/utils/pixKey` so the client-account form (BRL|Pix) shares
// the exact same behavior. Local aliases keep the panel code unchanged.
type PixType = PixKeyTypeEnum;
const PIX = PIX_KEY_META;
const PIX_LEGACY_TO_ENUM = PIX_KEY_TYPE_BY_LEGACY;
const pixValid = pixKeyValid;

/**
 * Shared plumbing for the runtime recipient panels (Brazil PIX / Mexico SPEI / Argentina CBU-CVU): the
 * verified-clients list and the selected client's accounts filtered by the destination currency. Selection
 * state lives in the dispersions form store; each panel keeps its own account→fields mapping.
 */
function useVerifiedClientAccounts(destinationCurrency: string) {
  const { formData, updateField, updateRecipient, verifiedClientIds, fetchVerifiedClientIds } =
    useDispersions();

  const [clients, setClients] = useState<{ id: string; displayName: string }[]>([]);
  const [clientsLoading, setClientsLoading] = useState(true);
  const [accounts, setAccounts] = useState<ClientAccount[]>([]);
  // Client whose accounts are already loaded — derives the "loaded" state
  // without a synchronous setState inside the effect.
  const [accountsClientId, setAccountsClientId] = useState<string | null>(null);
  // Clients registered through the recipient-step shortcut: not KYC-verified yet — flagged locally so the
  // badge shows even before/without the org-detail data.
  const [injectedClientIds, setInjectedClientIds] = useState<ReadonlySet<string>>(new Set());
  // Bumped after the shortcut creates an account so the effect refetches the (already selected) client.
  const [accountsVersion, setAccountsVersion] = useState(0);

  const clientId = formData.selectedClientId ?? "";
  const accountId = formData.selectedClientAccountId ?? "";

  useEffect(() => {
    let cancelled = false;
    // Server-side KYC FILTER disabled (business decision): the select lists EVERY registered client so
    // operators see the whole portfolio — unverified clients get a "Not verified" badge, a blocking note
    // when selected, and the step-2 gate (DispersionsListPage) prevents continuing until they verify.
    // Two facts back removing the param:
    //   1) The runtime payout (`POST /internal/payments/payouts`) carries no client reference and enforces
    //      no KYC — the gating is a frontend business rule (now enforced at step 2, not by hiding options).
    //   2) The `kyc_status: "VERIFIED"` param this fetch used to send was actually INERT: the backend list
    //      (`findAll` in coloca-nest `clients.internal.service.ts`) declares `kyc_status` in its query DTO
    //      but never applies it to the SQL, so the param filtered nothing.
    // The per-client status comes from the shared clientKyc slice (this list response carries no KYC data).
    ClientsService.fetchList({ skip: 0, limit: 100 })
      .then((res) => {
        if (cancelled) return;
        setClients(
          res.data.data.map((raw) => {
            const c = normalizeClientListItem(raw);
            return { id: c.id, displayName: clientDisplayName(c) };
          }),
        );
      })
      .catch(() => {
        if (!cancelled) setClients([]);
      })
      .finally(() => {
        if (!cancelled) setClientsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // The paginated list carries NO KYC data (no `identity.kyc_provider` join on the backend); the real
  // per-client status lives in the dispersions slice (fetched once per session from the organization
  // detail) so the panels AND the step-2 gate share the same source.
  useEffect(() => {
    fetchVerifiedClientIds();
  }, [fetchVerifiedClientIds]);

  const verifiedSet = useMemo(
    () => (verifiedClientIds === null ? null : new Set(verifiedClientIds)),
    [verifiedClientIds],
  );

  useEffect(() => {
    if (!clientId) return;
    let cancelled = false;
    ClientsService.fetchAccounts(clientId)
      .then((res) => {
        if (!cancelled) {
          setAccounts(res.data);
          setAccountsClientId(clientId);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setAccounts([]);
          setAccountsClientId(clientId);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [clientId, accountsVersion]);

  const accountsLoaded = !!clientId && accountsClientId === clientId;

  const filteredAccounts = useMemo(
    () => (clientId && accountsLoaded ? accounts.filter((a) => a.currency === destinationCurrency) : []),
    [accounts, destinationCurrency, clientId, accountsLoaded],
  );

  const handleClient = (id: string) => {
    // The select may fire onChange even when re-selecting the same client;
    // without this guard the already-chosen account would be lost.
    if (id === clientId) return;
    updateField("selectedClientId", id || null);
    updateField("selectedClientAccountId", null);
    updateField("selectedClientAccountMethod", null);
    updateRecipient("beneficiary", "");
  };

  /** Add a just-registered (KYC-pending) client to the local list and select it. Idempotent. */
  const injectClient = (client: { id: string; displayName: string }) => {
    setClients((prev) => (prev.some((c) => c.id === client.id) ? prev : [client, ...prev]));
    setInjectedClientIds((prev) => {
      if (prev.has(client.id)) return prev;
      const next = new Set(prev);
      next.add(client.id);
      return next;
    });
    // Same guard as handleClient: a repeated call must not wipe the account selection.
    if (client.id !== clientId) {
      updateField("selectedClientId", client.id);
      updateField("selectedClientAccountId", null);
      updateField("selectedClientAccountMethod", null);
      updateRecipient("beneficiary", "");
    }
  };

  const refreshAccounts = () => setAccountsVersion((v) => v + 1);

  /** Marks the option's "Not verified" badge and the dispersal block. Unknown (null) never flags — fail open. */
  const isClientUnverified = useCallback(
    (id: string) => injectedClientIds.has(id) || (verifiedSet !== null && !verifiedSet.has(id)),
    [injectedClientIds, verifiedSet],
  );

  return {
    clients,
    clientsLoading,
    clientId,
    accountId,
    filteredAccounts,
    accountsLoaded,
    handleClient,
    injectClient,
    refreshAccounts,
    isClientUnverified,
  };
}

// Faithful "Paso 2 · Destinatario" for the Brazil / PIX corridor, built with the
// design-system-aligned field primitives (Tailwind + dark mode).
const BrazilPixRecipient: React.FC<{ destinationCurrency: string }> = ({ destinationCurrency }) => {
  const { formData, updateField, updateRecipient } = useDispersions();
  const { t } = useTranslations("dispersions");
  // PIX placeholder/helper/error copy is shared across modules via `PIX_KEY_META` (global-namespace keys).
  const { t: tGlobal } = useTranslations("global");

  const {
    clients,
    clientsLoading,
    clientId,
    accountId,
    filteredAccounts,
    accountsLoaded,
    handleClient,
    injectClient,
    refreshAccounts,
    isClientUnverified,
  } = useVerifiedClientAccounts(destinationCurrency);

  // Stable options array: the select re-renders its menu only when this identity changes.
  const clientOptions = useMemo(
    () =>
      clients.map((c) => ({
        value: c.id,
        label: c.displayName,
        badge: isClientUnverified(c.id) ? t("client_not_verified_badge") : undefined,
      })),
    [clients, isClientUnverified, t],
  );

  const [pixTouched, setPixTouched] = useState(false);

  const rawPixType = String(formData.recipientFields?.pix_type ?? "CPF");
  // An unknown value (legacy metadata) falls back to CPF instead of breaking `PIX[...]`.
  const pixTypeCandidate = PIX_LEGACY_TO_ENUM[rawPixType] ?? rawPixType.toUpperCase();
  const pixType: PixType = pixTypeCandidate in PIX ? (pixTypeCandidate as PixType) : "CPF";
  const pixKey = String(formData.recipientFields?.pix_key ?? "");
  const beneficiary = String(formData.recipientFields?.beneficiary ?? "");
  const concept = String(formData.recipientFields?.description ?? "");

  const meta = PIX[pixType];
  const valid = pixValid(pixType, pixKey);
  const showPixError = !!pixKey && pixTouched && !valid;

  const selectedClientName = clients.find((c) => c.id === clientId)?.displayName;

  const isManual = formData.recipientMode === "manual";

  // Recipient-mode toggle. Both directions start from a clean slate — a half-applied autofill must
  // never leak into the manual payload (nor a manual draft into the client one).
  const handleModeChange = (mode: RecipientMode) => {
    updateField("recipientMode", mode);
    updateField("selectedClientId", null);
    updateField("selectedClientAccountId", null);
    updateField("selectedClientAccountMethod", null);
    updateRecipient("beneficiary", "");
    updateRecipient("pix_type", "CPF");
    updateRecipient("pix_key", "");
    updateRecipient("description", "");
    setPixTouched(false);
  };

  // Shared by the manual account selection and the register-client shortcut (which holds the account
  // object before the refetched list contains it).
  const applyAccount = (account: ClientAccount, beneficiaryName?: string) => {
    updateField("selectedClientAccountId", account.id);
    updateField("selectedClientAccountMethod", account.bank_name?.trim().toUpperCase() ?? null);
    if (beneficiaryName) updateRecipient("beneficiary", beneficiaryName);

    const md = account.account_metadata ?? {};
    const rawType = md["pix_type"] ?? md["tipoLlavePix"];
    let effectiveType: PixType = pixType;
    if (rawType !== undefined && rawType !== null && rawType !== "") {
      const upper = String(rawType).toUpperCase();
      const mapped = PIX_LEGACY_TO_ENUM[String(rawType)] ?? PIX_LEGACY_TO_ENUM[upper] ?? (upper as PixType);
      if (mapped in PIX) {
        effectiveType = mapped as PixType;
        updateRecipient("pix_type", mapped);
      }
    }
    // Freshly created accounts only carry the legacy `llavePix` metadata (the generated `account_key`
    // may be absent from the POST response) — same fallback chain as the payments module.
    const rawKey = md["pix_key"] ?? md["llavePix"] ?? account.account_key;
    // The key stored on the account may come in raw (uppercase, unmasked):
    // run it through the same formatter as manual typing so the view and
    // the validation stay consistent.
    if (rawKey) updateRecipient("pix_key", formatPixInput(effectiveType, String(rawKey)));
  };

  const handleAccount = (id: string) => {
    updateField("selectedClientAccountId", id || null);
    const account = filteredAccounts.find((a) => a.id === id);
    if (!account) {
      updateField("selectedClientAccountMethod", null);
      return;
    }
    applyAccount(account, selectedClientName);
  };

  const handleRegisteredClient = (client: CreatedClientSummary) => {
    injectClient({ id: client.id, displayName: client.displayName });
  };

  const handleRegisteredAccount = (client: CreatedClientSummary, account: ClientAccount) => {
    injectClient({ id: client.id, displayName: client.displayName });
    applyAccount(account, client.displayName);
    refreshAccounts();
  };

  // Register-account shortcut on the account select: same select+autofill path as a manual pick.
  const handleDrawerAccountCreated = (account: ClientAccount) => {
    applyAccount(account, selectedClientName);
    refreshAccounts();
  };

  // Rendered first in manual mode (takes the client select's slot), in its usual spot otherwise.
  // In client mode the destination data comes exclusively from the selected account (autofill), so the
  // field is read-only — only manual mode unlocks typing.
  const beneficiaryField = (
    <FieldInput
      id="benef"
      label={t("step2_recipient_name_label")}
      maxLength={120}
      placeholder={t("step2_recipient_name_placeholder")}
      value={beneficiary}
      disabled={!isManual}
      onValueChange={(value) => updateRecipient("beneficiary", value.slice(0, 120))}
    />
  );

  return (
    <div className="flex flex-col gap-5 text-foreground">
      {/* Type the recipient (default) or pick a registered client. */}
      <RecipientModeSwitch mode={formData.recipientMode} onModeChange={handleModeChange} />

      {!isManual && (
        <>
          {/* KYC verification unavailable → the step-2 gate is failing closed; offer the retry. */}
          <ClientKycUnavailableAlert />

          {/* Cliente */}
          <div className="flex flex-col gap-2">
            <FieldSelect
              id="cliente"
              label={t("step2_client_label")}
              labelAction={
                <RegisterClientAction
                  destinationCurrency={destinationCurrency}
                  onClientCreated={handleRegisteredClient}
                  onAccountCreated={handleRegisteredAccount}
                />
              }
              value={clientId}
              disabled={clientsLoading}
              placeholder={
                clientsLoading ? t("step2_client_loading_placeholder") : t("step2_client_placeholder")
              }
              options={clientOptions}
              onValueChange={handleClient}
            />
            {!!clientId && isClientUnverified(clientId) && (
              <ColocaAlert inline aria-live="polite" variant="warning" text={t("client_kyc_blocked_note")} />
            )}
          </div>

          {/* Cuenta del beneficiario */}
          <div className="flex flex-col gap-2">
            <FieldSelect
              id="cuenta"
              label={t("step2_account_label", { currency: destinationCurrency })}
              labelAction={
                clientId ? (
                  <RegisterAccountAction
                    clientId={clientId}
                    clientDisplayName={selectedClientName ?? ""}
                    destinationCurrency={destinationCurrency}
                    onAccountCreated={handleDrawerAccountCreated}
                  />
                ) : undefined
              }
              value={accountId}
              disabled={!clientId || filteredAccounts.length === 0}
              placeholder={t("step2_account_placeholder")}
              options={filteredAccounts.map((a) => ({
                value: a.id,
                label: `${a.bank_name ?? t("step2_account_fallback_label")} · ${a.currency ?? ""} · ${a.payment_method ?? "PIX"}`,
              }))}
              onValueChange={handleAccount}
            />
            {!!clientId && accountsLoaded && filteredAccounts.length === 0 && (
              <ColocaAlert
                inline
                aria-live="polite"
                variant="warning"
                text={t("step2_no_accounts_note", { currency: destinationCurrency })}
              />
            )}
          </div>
        </>
      )}

      {/* Beneficiario (modo pago único) — the backend caps beneficiary_name at 120 chars */}
      {isManual && beneficiaryField}

      {/* Separador dinámico */}
      <div className="mt-0.5 flex items-center gap-2.5">
        <div className="h-px flex-1 bg-border" />
        <div className="whitespace-nowrap text-[11px] font-semibold tracking-wider text-muted-foreground">
          {t("step2_separator_brazil")}
        </div>
        <div className="h-px flex-1 bg-border" />
      </div>

      {/* Tipo de clave + Clave — locked in client mode: filled by the account autofill only */}
      <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-[180px_1fr]">
        <FieldSelect
          id="tipoclave"
          label={t("step2_pix_type_label")}
          value={pixType}
          searchable={false}
          disabled={!isManual}
          // The descriptive type names reuse the step-3 keys so the step-2 select and the step-3 review
          // can never drift apart; CPF/CNPJ are proper nouns.
          options={[
            { value: "CPF", label: "CPF" },
            { value: "CNPJ", label: "CNPJ" },
            { value: "EMAIL", label: t("step3_pix_type_email") },
            { value: "PHONE", label: t("step3_pix_type_phone") },
            { value: "EVP", label: t("step3_pix_type_evp") },
          ]}
          onValueChange={(v) => {
            // Re-selecting the same type must not clear the typed key.
            if (v === pixType) return;
            updateRecipient("pix_type", v);
            updateRecipient("pix_key", "");
            setPixTouched(false);
          }}
        />
        <FieldInput
          id="clave"
          label={t("step3_pix_key_label")}
          placeholder={tGlobal(meta.ph)}
          value={pixKey}
          disabled={!isManual}
          onValueChange={(value) => updateRecipient("pix_key", formatPixInput(pixType, value))}
          onBlur={() => setPixTouched(true)}
          error={showPixError ? tGlobal(meta.err) : undefined}
          helper={tGlobal(meta.helper)}
        />
      </div>

      {/* Beneficiario — the backend caps beneficiary_name at 120 chars */}
      {!isManual && beneficiaryField}

      {/* Concepto */}
      <FieldInput
        id="concepto"
        label={t("concept_label")}
        optional
        maxLength={120}
        placeholder={t("step2_concept_placeholder")}
        value={concept}
        onValueChange={(value) => updateRecipient("description", value.slice(0, 120))}
      />
    </div>
  );
};

// CLABE input handling for the Mexico / SPEI corridor: digits only, 18 exact.
function formatClabeInput(raw: string): string {
  return raw.replace(/\D/g, "").slice(0, 18);
}

function clabeValid(clabe: string): boolean {
  return clabe.replace(/\D/g, "").length === 18;
}

// Faithful "Paso 2 · Destinatario" for the Mexico / SPEI corridor — mirror of
// BrazilPixRecipient (client → account autofill → manual fields), built with
// the same design-system-aligned field primitives.
const MexicoSpeiRecipient: React.FC<{ destinationCurrency: string }> = ({ destinationCurrency }) => {
  const { formData, updateField, updateRecipient } = useDispersions();
  const { t } = useTranslations("dispersions");
  const {
    clients,
    clientsLoading,
    clientId,
    accountId,
    filteredAccounts,
    accountsLoaded,
    handleClient,
    injectClient,
    refreshAccounts,
    isClientUnverified,
  } = useVerifiedClientAccounts(destinationCurrency);

  // Stable options array: the select re-renders its menu only when this identity changes.
  const clientOptions = useMemo(
    () =>
      clients.map((c) => ({
        value: c.id,
        label: c.displayName,
        badge: isClientUnverified(c.id) ? t("client_not_verified_badge") : undefined,
      })),
    [clients, isClientUnverified, t],
  );

  const [clabeTouched, setClabeTouched] = useState(false);

  const clabe = String(formData.recipientFields?.clabe ?? "");
  const beneficiary = String(formData.recipientFields?.beneficiary ?? "");
  const concept = String(formData.recipientFields?.description ?? "");

  const showClabeError = !!clabe && clabeTouched && !clabeValid(clabe);

  const selectedClientName = clients.find((c) => c.id === clientId)?.displayName;

  const isManual = formData.recipientMode === "manual";

  // Recipient-mode toggle. Both directions start from a clean slate — a half-applied autofill must
  // never leak into the manual payload (nor a manual draft into the client one).
  const handleModeChange = (mode: RecipientMode) => {
    updateField("recipientMode", mode);
    updateField("selectedClientId", null);
    updateField("selectedClientAccountId", null);
    updateField("selectedClientAccountMethod", null);
    updateRecipient("beneficiary", "");
    updateRecipient("clabe", "");
    updateRecipient("description", "");
    setClabeTouched(false);
  };

  // Shared by the manual account selection and the register-client shortcut (which holds the account
  // object before the refetched list contains it).
  const applyAccount = (account: ClientAccount, beneficiaryName?: string) => {
    updateField("selectedClientAccountId", account.id);
    updateField("selectedClientAccountMethod", account.bank_name?.trim().toUpperCase() ?? null);
    if (beneficiaryName) updateRecipient("beneficiary", beneficiaryName);

    // The CLABE stored on the account may live in the metadata or in the
    // account key: run it through the same formatter as manual typing so the
    // view and the validation stay consistent.
    const md = account.account_metadata ?? {};
    const rawClabe = md["clabe"] ?? account.account_key;
    if (rawClabe) updateRecipient("clabe", formatClabeInput(String(rawClabe)));
  };

  const handleAccount = (id: string) => {
    updateField("selectedClientAccountId", id || null);
    const account = filteredAccounts.find((a) => a.id === id);
    if (!account) {
      updateField("selectedClientAccountMethod", null);
      return;
    }
    applyAccount(account, selectedClientName);
  };

  const handleRegisteredClient = (client: CreatedClientSummary) => {
    injectClient({ id: client.id, displayName: client.displayName });
  };

  const handleRegisteredAccount = (client: CreatedClientSummary, account: ClientAccount) => {
    injectClient({ id: client.id, displayName: client.displayName });
    applyAccount(account, client.displayName);
    refreshAccounts();
  };

  // Register-account shortcut on the account select: same select+autofill path as a manual pick.
  const handleDrawerAccountCreated = (account: ClientAccount) => {
    applyAccount(account, selectedClientName);
    refreshAccounts();
  };

  // Rendered first in manual mode (takes the client select's slot), in its usual spot otherwise.
  // In client mode the destination data comes exclusively from the selected account (autofill), so the
  // field is read-only — only manual mode unlocks typing.
  const beneficiaryField = (
    <FieldInput
      id="benef"
      label={t("step2_recipient_name_label")}
      maxLength={120}
      placeholder={t("step2_recipient_name_placeholder")}
      value={beneficiary}
      disabled={!isManual}
      onValueChange={(value) => updateRecipient("beneficiary", value.slice(0, 120))}
    />
  );

  return (
    <div className="flex flex-col gap-5 text-foreground">
      {/* Type the recipient (default) or pick a registered client. */}
      <RecipientModeSwitch mode={formData.recipientMode} onModeChange={handleModeChange} />

      {!isManual && (
        <>
          {/* KYC verification unavailable → the step-2 gate is failing closed; offer the retry. */}
          <ClientKycUnavailableAlert />

          {/* Cliente */}
          <div className="flex flex-col gap-2">
            <FieldSelect
              id="cliente"
              label={t("step2_client_label")}
              labelAction={
                <RegisterClientAction
                  destinationCurrency={destinationCurrency}
                  onClientCreated={handleRegisteredClient}
                  onAccountCreated={handleRegisteredAccount}
                />
              }
              value={clientId}
              disabled={clientsLoading}
              placeholder={
                clientsLoading ? t("step2_client_loading_placeholder") : t("step2_client_placeholder")
              }
              options={clientOptions}
              onValueChange={handleClient}
            />
            {!!clientId && isClientUnverified(clientId) && (
              <ColocaAlert inline aria-live="polite" variant="warning" text={t("client_kyc_blocked_note")} />
            )}
          </div>

          {/* Cuenta del beneficiario */}
          <div className="flex flex-col gap-2">
            <FieldSelect
              id="cuenta"
              label={t("step2_account_label", { currency: destinationCurrency })}
              labelAction={
                clientId ? (
                  <RegisterAccountAction
                    clientId={clientId}
                    clientDisplayName={selectedClientName ?? ""}
                    destinationCurrency={destinationCurrency}
                    onAccountCreated={handleDrawerAccountCreated}
                  />
                ) : undefined
              }
              value={accountId}
              disabled={!clientId || filteredAccounts.length === 0}
              placeholder={t("step2_account_placeholder")}
              options={filteredAccounts.map((a) => ({
                value: a.id,
                label: `${a.bank_name ?? t("step2_account_fallback_label")} · ${a.currency ?? ""} · ${a.payment_method ?? "SPEI"}`,
              }))}
              onValueChange={handleAccount}
            />
            {!!clientId && accountsLoaded && filteredAccounts.length === 0 && (
              <ColocaAlert
                inline
                aria-live="polite"
                variant="warning"
                text={t("step2_no_accounts_note", { currency: destinationCurrency })}
              />
            )}
          </div>
        </>
      )}

      {/* Beneficiario (modo pago único) — the backend caps beneficiary_name at 120 chars */}
      {isManual && beneficiaryField}

      {/* Separador dinámico */}
      <div className="mt-0.5 flex items-center gap-2.5">
        <div className="h-px flex-1 bg-border" />
        <div className="whitespace-nowrap text-[11px] font-semibold tracking-wider text-muted-foreground">
          {t("step2_separator_mexico")}
        </div>
        <div className="h-px flex-1 bg-border" />
      </div>

      {/* CLABE — locked in client mode: filled by the account autofill only */}
      <FieldInput
        id="clabe"
        label={t("step3_clabe_label")}
        placeholder={t("step2_clabe_placeholder")}
        value={clabe}
        disabled={!isManual}
        onValueChange={(value) => updateRecipient("clabe", formatClabeInput(value))}
        onBlur={() => setClabeTouched(true)}
        error={showClabeError ? t("step2_clabe_error") : undefined}
        helper={t("step2_clabe_helper")}
      />

      {/* Beneficiario — the backend caps beneficiary_name at 120 chars */}
      {!isManual && beneficiaryField}

      {/* Concepto */}
      <FieldInput
        id="concepto"
        label={t("concept_label")}
        optional
        maxLength={120}
        placeholder={t("step2_concept_placeholder")}
        value={concept}
        onValueChange={(value) => updateRecipient("description", value.slice(0, 120))}
      />
    </div>
  );
};

// CBU/CVU input handling for the Argentina corridor: digits only, 22 exact.
function formatCbuCvuInput(raw: string): string {
  return raw.replace(/\D/g, "").slice(0, 22);
}

function cbuCvuValid(value: string): boolean {
  return value.replace(/\D/g, "").length === 22;
}

// CUIT/CUIL: 11 digits when provided (optional field — the backend forwards it verbatim).
function formatCuitInput(raw: string): string {
  return raw.replace(/\D/g, "").slice(0, 11);
}

// Faithful "Paso 2 · Destinatario" for the Argentina / CBU_CVU corridor — mirror
// of MexicoSpeiRecipient (client → account autofill → manual fields), built with
// the same design-system-aligned field primitives.
const ArgentinaCbuCvuRecipient: React.FC<{ destinationCurrency: string }> = ({ destinationCurrency }) => {
  const { formData, updateField, updateRecipient } = useDispersions();
  const { t } = useTranslations("dispersions");
  const {
    clients,
    clientsLoading,
    clientId,
    accountId,
    filteredAccounts,
    accountsLoaded,
    handleClient,
    injectClient,
    refreshAccounts,
    isClientUnverified,
  } = useVerifiedClientAccounts(destinationCurrency);

  // Stable options array: react-select re-renders its menu only when this identity changes.
  const clientOptions = useMemo(
    () =>
      clients.map((c) => ({
        value: c.id,
        label: c.displayName,
        badge: isClientUnverified(c.id) ? t("client_not_verified_badge") : undefined,
      })),
    [clients, isClientUnverified, t],
  );

  const [accountTouched, setAccountTouched] = useState(false);
  const [cuitTouched, setCuitTouched] = useState(false);

  // Account id type defaults to CVU (wallet transfers are the common case in Argentina) — through the shared
  // resolver, so what this select SHOWS and what the payout DISPATCHES cannot default differently.
  const accountType = resolveCbuCvuKind(formData.recipientFields?.account_type) ?? "CVU";
  const accountNumber = String(formData.recipientFields?.account_number ?? "");
  const cuit = String(formData.recipientFields?.cuit ?? "");
  const beneficiary = String(formData.recipientFields?.beneficiary ?? "");
  const concept = String(formData.recipientFields?.description ?? "");

  const showAccountError = !!accountNumber && accountTouched && !cbuCvuValid(accountNumber);
  // Optional field, but a PARTIAL value still blocks the step-2 gate — surface why instead of leaving
  // the Continue button disabled with no visible error.
  const showCuitError = !!cuit && cuitTouched && cuit.length !== 11;

  const selectedClientName = clients.find((c) => c.id === clientId)?.displayName;

  const isManual = formData.recipientMode === "manual";

  // Recipient-mode toggle. Both directions start from a clean slate — a half-applied autofill must
  // never leak into the manual payload (nor a manual draft into the client one).
  const handleModeChange = (mode: RecipientMode) => {
    updateField("recipientMode", mode);
    updateField("selectedClientId", null);
    updateField("selectedClientAccountId", null);
    updateField("selectedClientAccountMethod", null);
    updateRecipient("beneficiary", "");
    updateRecipient("account_type", "CVU");
    updateRecipient("account_number", "");
    updateRecipient("cuit", "");
    updateRecipient("description", "");
    setAccountTouched(false);
    setCuitTouched(false);
  };

  // Shared by the manual account selection and the register-client shortcut (which holds the account
  // object before the refetched list contains it).
  const applyAccount = (account: ClientAccount, beneficiaryName?: string) => {
    updateField("selectedClientAccountId", account.id);
    updateField("selectedClientAccountMethod", account.bank_name?.trim().toUpperCase() ?? null);
    if (beneficiaryName) updateRecipient("beneficiary", beneficiaryName);

    // ARS accounts store the number under the method key ("cbu" / "cvu"), the legacy combined key
    // ("cbuCvu") or the generated account_key; the type comes from the bank method with the metadata
    // key as fallback. Run it through the same formatter as manual typing so the view and the
    // validation stay consistent.
    const md = account.account_metadata ?? {};
    const bankMethod = (account.bank_name ?? "").trim().toUpperCase();
    const effectiveType =
      bankMethod === "CBU" || bankMethod === "CVU" ? bankMethod : md["cbu"] ? "CBU" : "CVU";
    updateRecipient("account_type", effectiveType);
    const rawValue = md["cbu"] ?? md["cvu"] ?? md["cbuCvu"] ?? account.account_key;
    if (rawValue) updateRecipient("account_number", formatCbuCvuInput(String(rawValue)));
  };

  const handleAccount = (id: string) => {
    updateField("selectedClientAccountId", id || null);
    const account = filteredAccounts.find((a) => a.id === id);
    if (!account) {
      updateField("selectedClientAccountMethod", null);
      return;
    }
    applyAccount(account, selectedClientName);
  };

  const handleRegisteredClient = (client: CreatedClientSummary) => {
    injectClient({ id: client.id, displayName: client.displayName });
  };

  const handleRegisteredAccount = (client: CreatedClientSummary, account: ClientAccount) => {
    injectClient({ id: client.id, displayName: client.displayName });
    applyAccount(account, client.displayName);
    refreshAccounts();
  };

  // Register-account shortcut on the account select: same select+autofill path as a manual pick.
  const handleDrawerAccountCreated = (account: ClientAccount) => {
    applyAccount(account, selectedClientName);
    refreshAccounts();
  };

  // Rendered first in manual mode (takes the client select's slot), in its usual spot otherwise.
  // In client mode the destination data comes exclusively from the selected account (autofill), so the
  // field is read-only — only manual mode unlocks typing.
  const beneficiaryField = (
    <FieldInput
      id="benef"
      label={t("step2_recipient_name_label")}
      maxLength={120}
      placeholder={t("step2_recipient_name_placeholder")}
      value={beneficiary}
      disabled={!isManual}
      onValueChange={(value) => updateRecipient("beneficiary", value.slice(0, 120))}
    />
  );

  return (
    <div className="flex flex-col gap-5 text-foreground">
      {/* Type the recipient (default) or pick a registered client. */}
      <RecipientModeSwitch mode={formData.recipientMode} onModeChange={handleModeChange} />

      {!isManual && (
        <>
          {/* KYC verification unavailable → the step-2 gate is failing closed; offer the retry. */}
          <ClientKycUnavailableAlert />

          {/* Cliente */}
          <div className="flex flex-col gap-2">
            <FieldSelect
              id="cliente"
              label={t("step2_client_label")}
              labelAction={
                <RegisterClientAction
                  destinationCurrency={destinationCurrency}
                  onClientCreated={handleRegisteredClient}
                  onAccountCreated={handleRegisteredAccount}
                />
              }
              value={clientId}
              disabled={clientsLoading}
              placeholder={
                clientsLoading ? t("step2_client_loading_placeholder") : t("step2_client_placeholder")
              }
              options={clientOptions}
              onValueChange={handleClient}
            />
            {!!clientId && isClientUnverified(clientId) && (
              <ColocaAlert inline aria-live="polite" variant="warning" text={t("client_kyc_blocked_note")} />
            )}
          </div>

          {/* Cuenta del beneficiario */}
          <div className="flex flex-col gap-2">
            <FieldSelect
              id="cuenta"
              label={t("step2_account_label", { currency: destinationCurrency })}
              labelAction={
                clientId ? (
                  <RegisterAccountAction
                    clientId={clientId}
                    clientDisplayName={selectedClientName ?? ""}
                    destinationCurrency={destinationCurrency}
                    onAccountCreated={handleDrawerAccountCreated}
                  />
                ) : undefined
              }
              value={accountId}
              disabled={!clientId || filteredAccounts.length === 0}
              placeholder={t("step2_account_placeholder")}
              options={filteredAccounts.map((a) => ({
                value: a.id,
                label: `${a.bank_name ?? t("step2_account_fallback_label")} · ${a.currency ?? ""} · ${a.payment_method ?? "CBU/CVU"}`,
              }))}
              onValueChange={handleAccount}
            />
            {!!clientId && accountsLoaded && filteredAccounts.length === 0 && (
              <ColocaAlert
                inline
                aria-live="polite"
                variant="warning"
                text={t("step2_no_accounts_note", { currency: destinationCurrency })}
              />
            )}
          </div>
        </>
      )}

      {/* Beneficiario (modo pago único) — the backend caps beneficiary_name at 120 chars */}
      {isManual && beneficiaryField}

      {/* Separador dinámico */}
      <div className="mt-0.5 flex items-center gap-2.5">
        <div className="h-px flex-1 bg-border" />
        <div className="whitespace-nowrap text-[11px] font-semibold tracking-wider text-muted-foreground">
          {t("step2_separator_argentina")}
        </div>
        <div className="h-px flex-1 bg-border" />
      </div>

      {/* Tipo de cuenta + número — locked in client mode: filled by the account autofill only */}
      <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-[180px_1fr]">
        <FieldSelect
          id="tipocuenta"
          // Same key as the step-3 review row, so the form and the confirmation can never drift apart.
          label={t("step3_account_type_label")}
          value={accountType}
          searchable={false}
          disabled={!isManual}
          options={[
            { value: "CVU", label: t("step2_account_type_cvu") },
            { value: "CBU", label: t("step2_account_type_cbu") },
          ]}
          onValueChange={(v) => {
            // Re-selecting the same type must not clear the typed number.
            if (v === accountType) return;
            updateRecipient("account_type", v);
            updateRecipient("account_number", "");
            setAccountTouched(false);
          }}
        />
        <FieldInput
          id="cuentanumero"
          label={accountType === "CBU" ? "CBU" : "CVU"}
          placeholder={t("step2_cbu_cvu_placeholder")}
          value={accountNumber}
          disabled={!isManual}
          onValueChange={(value) => updateRecipient("account_number", formatCbuCvuInput(value))}
          onBlur={() => setAccountTouched(true)}
          error={showAccountError ? t("step2_cbu_cvu_error", { type: accountType }) : undefined}
          helper={t("step2_cbu_cvu_helper")}
        />
      </div>

      {/* CUIT/CUIL — optional, not stored on the account metadata, so it stays editable in both modes */}
      <FieldInput
        id="cuit"
        label={t("step2_cuit_label")}
        optional
        placeholder={t("step2_cuit_placeholder")}
        value={cuit}
        onValueChange={(value) => updateRecipient("cuit", formatCuitInput(value))}
        onBlur={() => setCuitTouched(true)}
        error={showCuitError ? t("step2_cuit_error") : undefined}
        helper={t("step2_cuit_helper")}
      />

      {/* Beneficiario — the backend caps beneficiary_name at 120 chars */}
      {!isManual && beneficiaryField}

      {/* Concepto */}
      <FieldInput
        id="concepto"
        label={t("concept_label")}
        optional
        maxLength={120}
        placeholder={t("step2_concept_placeholder")}
        value={concept}
        onValueChange={(value) => updateRecipient("description", value.slice(0, 120))}
      />
    </div>
  );
};

// Bre-B key input masking per key type (Colombia). PHONE → digits (10); NRIC → alphanumerics (document);
// ALPHANUM/MERCHANT → no whitespace; EMAIL → kept as typed (normalized to lowercase only on submit).
function formatBrebKeyInput(type: BrebKeyType, raw: string): string {
  switch (type) {
    case BrebKeyType.PHONE:
      return raw.replace(/\D/g, "").slice(0, 10);
    case BrebKeyType.NRIC:
      return raw.replace(/[^a-zA-Z0-9]/g, "").slice(0, 20);
    case BrebKeyType.ALPHANUM:
    case BrebKeyType.MERCHANT:
      return raw.replace(/\s+/g, "").slice(0, 64);
    case BrebKeyType.EMAIL:
    default:
      return raw.trimStart().slice(0, 255);
  }
}

// i18n key maps per Bre-B key type — literal-typed so `t()` accepts them without importing the interface.
const BREB_KEY_TYPE_OPTION_KEY = {
  PHONE: "breb_key_type_phone",
  EMAIL: "breb_key_type_email",
  ALPHANUM: "breb_key_type_alphanum",
  NRIC: "breb_key_type_nric",
  MERCHANT: "breb_key_type_merchant",
} as const;
const BREB_KEY_PLACEHOLDER_KEY = {
  PHONE: "step2_breb_key_placeholder_phone",
  EMAIL: "step2_breb_key_placeholder_email",
  ALPHANUM: "step2_breb_key_placeholder_alphanum",
  NRIC: "step2_breb_key_placeholder_nric",
  MERCHANT: "step2_breb_key_placeholder_merchant",
} as const;
const BREB_KEY_ERROR_KEY = {
  PHONE: "step2_breb_key_error_phone",
  EMAIL: "step2_breb_key_error_email",
  ALPHANUM: "step2_breb_key_error_alphanum",
  NRIC: "step2_breb_key_error_nric",
  MERCHANT: "step2_breb_key_error_merchant",
} as const;

// Faithful "Paso 2 · Destinatario" for the Colombia / Bre-B corridor — mirror of ArgentinaCbuCvuRecipient
// (client → account autofill → manual fields), swapping the CBU/CVU pair for a Bre-B key-type + key-value
// pair. Bre-B has no CUIT-equivalent; the key alias resolves the beneficiary in the DICE directory.
const ColombiaBrebRecipient: React.FC<{ destinationCurrency: string }> = ({ destinationCurrency }) => {
  const { formData, updateField, updateRecipient } = useDispersions();
  const { t } = useTranslations("dispersions");
  const {
    clients,
    clientsLoading,
    clientId,
    accountId,
    filteredAccounts,
    accountsLoaded,
    handleClient,
    injectClient,
    refreshAccounts,
    isClientUnverified,
  } = useVerifiedClientAccounts(destinationCurrency);

  // Stable options array: the select re-renders its menu only when this identity changes.
  const clientOptions = useMemo(
    () =>
      clients.map((c) => ({
        value: c.id,
        label: c.displayName,
        badge: isClientUnverified(c.id) ? t("client_not_verified_badge") : undefined,
      })),
    [clients, isClientUnverified, t],
  );

  const [keyTouched, setKeyTouched] = useState(false);

  // Key type defaults to PHONE (a mobile alias is the most common Bre-B key).
  const rawKeyType = String(formData.recipientFields?.breb_key_type ?? "PHONE").toUpperCase();
  const keyType: BrebKeyType = (Object.values(BrebKeyType) as string[]).includes(rawKeyType)
    ? (rawKeyType as BrebKeyType)
    : BrebKeyType.PHONE;
  const keyValue = String(formData.recipientFields?.breb_key_value ?? "");
  const concept = String(formData.recipientFields?.description ?? "");
  // No `beneficiary` here on purpose: the directory owns the recipient's name on this rail, so nothing in
  // this panel reads or writes the form's name field. `handleModeChange` still clears it, because switching
  // modes must not leave a value another rail typed.

  const showKeyError = !!keyValue && keyTouched && !isBrebKeyValid(keyType, keyValue);

  // Directory confirmation (masked holder identity) — fires once the key is valid, in both modes:
  // an autofilled client key deserves the same verification as a typed one.
  const resolution = useBrebKeyResolution(keyType, keyValue, true);

  const selectedClientName = clients.find((c) => c.id === clientId)?.displayName;

  const isManual = formData.recipientMode === "manual";

  // Recipient-mode toggle. Both directions start from a clean slate — a half-applied autofill must
  // never leak into the manual payload (nor a manual draft into the client one).
  const handleModeChange = (mode: RecipientMode) => {
    updateField("recipientMode", mode);
    updateField("selectedClientId", null);
    updateField("selectedClientAccountId", null);
    updateField("selectedClientAccountMethod", null);
    updateRecipient("beneficiary", "");
    updateRecipient("breb_key_type", BrebKeyType.PHONE);
    updateRecipient("breb_key_value", "");
    updateRecipient("description", "");
    setKeyTouched(false);
  };

  // Shared by the manual account selection and the register-client shortcut (which holds the account
  // object before the refetched list contains it).
  const applyAccount = (account: ClientAccount, beneficiaryName?: string) => {
    updateField("selectedClientAccountId", account.id);
    updateField("selectedClientAccountMethod", account.bank_name?.trim().toUpperCase() ?? null);
    if (beneficiaryName) updateRecipient("beneficiary", beneficiaryName);

    // COP/Bre-B accounts store the key under `llaveBreB` (or the generated account_key); the type is not
    // stored, so infer it from the value. Run it through the same formatter as manual typing so the view
    // and the validation stay consistent.
    const md = account.account_metadata ?? {};
    const rawKey = md["llaveBreB"] ?? md["llaveBreb"] ?? account.account_key;
    if (rawKey) {
      const inferred = inferBrebKeyType(String(rawKey));
      updateRecipient("breb_key_type", inferred);
      updateRecipient("breb_key_value", formatBrebKeyInput(inferred, String(rawKey)));
    }
  };

  const handleAccount = (id: string) => {
    updateField("selectedClientAccountId", id || null);
    const account = filteredAccounts.find((a) => a.id === id);
    if (!account) {
      updateField("selectedClientAccountMethod", null);
      return;
    }
    applyAccount(account, selectedClientName);
  };

  const handleRegisteredClient = (client: CreatedClientSummary) => {
    injectClient({ id: client.id, displayName: client.displayName });
  };

  const handleRegisteredAccount = (client: CreatedClientSummary, account: ClientAccount) => {
    injectClient({ id: client.id, displayName: client.displayName });
    applyAccount(account, client.displayName);
    refreshAccounts();
  };

  // Register-account shortcut on the account select: same select+autofill path as a manual pick.
  const handleDrawerAccountCreated = (account: ClientAccount) => {
    applyAccount(account, selectedClientName);
    refreshAccounts();
  };

  return (
    <div className="flex flex-col gap-5 text-foreground">
      {/* Type the recipient (default) or pick a registered client. */}
      <RecipientModeSwitch mode={formData.recipientMode} onModeChange={handleModeChange} />

      {!isManual && (
        <>
          {/* KYC verification unavailable → the step-2 gate is failing closed; offer the retry. */}
          <ClientKycUnavailableAlert />

          {/* Cliente */}
          <div className="flex flex-col gap-2">
            <FieldSelect
              id="cliente"
              label={t("step2_client_label")}
              labelAction={
                <RegisterClientAction
                  destinationCurrency={destinationCurrency}
                  onClientCreated={handleRegisteredClient}
                  onAccountCreated={handleRegisteredAccount}
                />
              }
              value={clientId}
              disabled={clientsLoading}
              placeholder={
                clientsLoading ? t("step2_client_loading_placeholder") : t("step2_client_placeholder")
              }
              options={clientOptions}
              onValueChange={handleClient}
            />
            {!!clientId && isClientUnverified(clientId) && (
              <ColocaAlert inline aria-live="polite" variant="warning" text={t("client_kyc_blocked_note")} />
            )}
          </div>

          {/* Cuenta del beneficiario */}
          <div className="flex flex-col gap-2">
            <FieldSelect
              id="cuenta"
              label={t("step2_account_label", { currency: destinationCurrency })}
              labelAction={
                clientId ? (
                  <RegisterAccountAction
                    clientId={clientId}
                    clientDisplayName={selectedClientName ?? ""}
                    destinationCurrency={destinationCurrency}
                    onAccountCreated={handleDrawerAccountCreated}
                  />
                ) : undefined
              }
              value={accountId}
              disabled={!clientId || filteredAccounts.length === 0}
              placeholder={t("step2_account_placeholder")}
              options={filteredAccounts.map((a) => ({
                value: a.id,
                label: `${a.bank_name ?? t("step2_account_fallback_label")} · ${a.currency ?? ""} · ${a.payment_method ?? "Bre-B"}`,
              }))}
              onValueChange={handleAccount}
            />
            {!!clientId && accountsLoaded && filteredAccounts.length === 0 && (
              <ColocaAlert
                inline
                aria-live="polite"
                variant="warning"
                text={t("step2_no_accounts_note", { currency: destinationCurrency })}
              />
            )}
          </div>
        </>
      )}

      {/* Section heading: on this rail the recipient is identified by the key alone, so the question the
          step answers is named out loud instead of being implied by a name field. */}
      <div className="mt-0.5 flex items-center gap-2.5">
        <div className="h-px flex-1 bg-border" />
        <div className="whitespace-nowrap text-[11px] font-semibold tracking-wider text-muted-foreground">
          {t("step2_recipient_section_title")}
        </div>
        <div className="h-px flex-1 bg-border" />
      </div>

      {/* Tipo de llave + valor — locked in client mode: filled by the account autofill only */}
      <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-[200px_1fr]">
        <FieldSelect
          id="tipollave"
          // Same key as the step-3 review row, so the form and the confirmation can never drift apart.
          label={t("step3_breb_key_type_label")}
          value={keyType}
          searchable={false}
          disabled={!isManual}
          options={[
            { value: BrebKeyType.PHONE, label: t(BREB_KEY_TYPE_OPTION_KEY.PHONE) },
            { value: BrebKeyType.EMAIL, label: t(BREB_KEY_TYPE_OPTION_KEY.EMAIL) },
            { value: BrebKeyType.ALPHANUM, label: t(BREB_KEY_TYPE_OPTION_KEY.ALPHANUM) },
            { value: BrebKeyType.NRIC, label: t(BREB_KEY_TYPE_OPTION_KEY.NRIC) },
            { value: BrebKeyType.MERCHANT, label: t(BREB_KEY_TYPE_OPTION_KEY.MERCHANT) },
          ]}
          onValueChange={(v) => {
            // Re-selecting the same type must not clear the typed key.
            if (v === keyType) return;
            updateRecipient("breb_key_type", v);
            updateRecipient("breb_key_value", "");
            setKeyTouched(false);
          }}
        />
        <FieldInput
          id="llave"
          label={t("step2_breb_key_label")}
          type={keyType === BrebKeyType.EMAIL ? "email" : "text"}
          // Only PHONE is digits-only. NRIC/ALPHANUM/MERCHANT accept letters (the mask keeps alphanumerics),
          // so a numeric keypad would block valid characters on mobile — keep the default text keyboard.
          inputMode={keyType === BrebKeyType.PHONE ? "numeric" : undefined}
          placeholder={t(BREB_KEY_PLACEHOLDER_KEY[keyType])}
          value={keyValue}
          disabled={!isManual}
          onValueChange={(value) => updateRecipient("breb_key_value", formatBrebKeyInput(keyType, value))}
          onBlur={() => setKeyTouched(true)}
          error={showKeyError ? t(BREB_KEY_ERROR_KEY[keyType]) : undefined}
          helper={t("step2_breb_key_helper")}
        />
      </div>

      {/* Who the directory says owns the key. This replaced the name field on this rail: the form no
          longer asks for a name it can look up, and a key the directory refuses — or cannot be asked
          about — blocks Continue from `useDispersionFormErrors`. */}
      <BrebRecipientConfirmation
        resolution={resolution}
        clientName={!isManual ? (selectedClientName ?? null) : null}
      />

      {/* Concepto */}
      <FieldInput
        id="concepto"
        label={t("concept_label")}
        optional
        maxLength={120}
        placeholder={t("step2_concept_placeholder")}
        value={concept}
        onValueChange={(value) => updateRecipient("description", value.slice(0, 120))}
      />
    </div>
  );
};

// Account-type labels for the ACH en línea (Transfiya) selector — literal-typed i18n keys.
const TRANSFIYA_ACCOUNT_TYPE_OPTION_KEY = {
  [TransfiyaAccountType.SAVINGS]: "tf_account_type_savings",
  [TransfiyaAccountType.CHECKING]: "tf_account_type_checking",
  [TransfiyaAccountType.DEPOSIT]: "tf_account_type_deposit",
  [TransfiyaAccountType.STANDARD_DEPOSIT]: "tf_account_type_standard_deposit",
  [TransfiyaAccountType.INCLUSIVE_DEPOSIT]: "tf_account_type_inclusive_deposit",
} as const;

/**
 * "Paso 2 · Destinatario" for the Colombia / TRANSFIYA (ACH en línea) corridor: an account-to-account credit
 * addressed by bank + account, with the beneficiary identified by their Colombian document. v1 is manual
 * entry only (no saved-client autofill — client accounts store Bre-B keys, not bank accounts).
 */
const ColombiaTransfiyaRecipient: React.FC = () => {
  const { formData, updateRecipient } = useDispersions();
  const { t } = useTranslations("dispersions");
  const [banks, setBanks] = useState<TransfiyaBank[] | null>(null);
  const [banksError, setBanksError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    TransfiyaBanksService.list()
      .then((res) => {
        if (!cancelled) setBanks(res.data);
      })
      .catch(() => {
        if (!cancelled) setBanksError(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const accountTypeRaw = String(formData.recipientFields?.transfiya_account_type ?? "svgs");
  const accountType = (Object.values(TransfiyaAccountType) as string[]).includes(accountTypeRaw)
    ? (accountTypeRaw as TransfiyaAccountType)
    : TransfiyaAccountType.SAVINGS;
  const accountNumber = String(formData.recipientFields?.transfiya_account_number ?? "");
  const bankCode = String(formData.recipientFields?.transfiya_bank_code ?? "");
  const docTypeRaw = String(formData.recipientFields?.transfiya_document_type ?? "CC").toUpperCase();
  const docType = (Object.values(ColombianDocumentType) as string[]).includes(docTypeRaw)
    ? (docTypeRaw as ColombianDocumentType)
    : ColombianDocumentType.CC;
  const docNumber = String(formData.recipientFields?.transfiya_document_number ?? "");
  const beneficiary = String(formData.recipientFields?.beneficiary ?? "");
  const concept = String(formData.recipientFields?.description ?? "");

  return (
    <div className="flex flex-col gap-5 text-foreground">
      {/* Beneficiario — required by the TRANSFIYA destination (the receiving bank displays it) */}
      <FieldInput
        id="tf-benef"
        label={t("step2_recipient_name_label")}
        maxLength={120}
        placeholder={t("step2_recipient_name_placeholder")}
        value={beneficiary}
        onValueChange={(value) => updateRecipient("beneficiary", value.slice(0, 120))}
      />

      {/* Documento del beneficiario */}
      <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-[200px_1fr]">
        <FieldSelect
          id="tf-doctype"
          label={t("step2_tf_doc_type_label")}
          value={docType}
          searchable={false}
          options={(Object.values(ColombianDocumentType) as string[]).map((code) => ({
            value: code,
            label: code,
          }))}
          onValueChange={(v) => updateRecipient("transfiya_document_type", v)}
        />
        <FieldInput
          id="tf-docnumber"
          label={t("step2_tf_doc_number_label")}
          inputMode="numeric"
          maxLength={20}
          placeholder={t("step2_tf_doc_number_placeholder")}
          value={docNumber}
          onValueChange={(value) => updateRecipient("transfiya_document_number", value.replace(/[^\d]/g, ""))}
        />
      </div>

      {/* Separador dinámico */}
      <div className="mt-0.5 flex items-center gap-2.5">
        <div className="h-px flex-1 bg-border" />
        <div className="whitespace-nowrap text-[11px] font-semibold tracking-wider text-muted-foreground">
          {t("step2_separator_colombia_transfiya")}
        </div>
        <div className="h-px flex-1 bg-border" />
      </div>

      {/* Banco destino (directorio de participantes) */}
      <div className="flex flex-col gap-2">
        <FieldSelect
          id="tf-bank"
          label={t("step2_tf_bank_label")}
          value={bankCode}
          disabled={banks === null && !banksError}
          placeholder={
            banks === null && !banksError ? t("step2_tf_bank_loading") : t("step2_tf_bank_placeholder")
          }
          options={(banks ?? []).map((bank) => ({ value: bank.code, label: bank.name }))}
          onValueChange={(v) => {
            updateRecipient("transfiya_bank_code", v);
            updateRecipient("transfiya_bank_name", (banks ?? []).find((b) => b.code === v)?.name ?? "");
          }}
        />
        {banksError && (
          <ColocaAlert inline aria-live="polite" variant="warning" text={t("step2_tf_bank_error")} />
        )}
      </div>

      {/* Tipo + número de cuenta */}
      <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-[220px_1fr]">
        <FieldSelect
          id="tf-accounttype"
          label={t("step2_tf_account_type_label")}
          value={accountType}
          searchable={false}
          options={(Object.values(TransfiyaAccountType) as TransfiyaAccountType[]).map((code) => ({
            value: code,
            label: t(TRANSFIYA_ACCOUNT_TYPE_OPTION_KEY[code]),
          }))}
          onValueChange={(v) => updateRecipient("transfiya_account_type", v)}
        />
        <FieldInput
          id="tf-accountnumber"
          label={t("step2_tf_account_number_label")}
          inputMode="numeric"
          maxLength={34}
          placeholder={t("step2_tf_account_number_placeholder")}
          value={accountNumber}
          onValueChange={(value) => updateRecipient("transfiya_account_number", value.replace(/[^\d]/g, ""))}
        />
      </div>

      {/* Concepto */}
      <FieldInput
        id="tf-concepto"
        label={t("concept_label")}
        optional
        maxLength={120}
        placeholder={t("step2_concept_placeholder")}
        value={concept}
        onValueChange={(value) => updateRecipient("description", value.slice(0, 120))}
      />
    </div>
  );
};

/**
 * Colombia panel: BREB (Bre-B key, default) vs TRANSFIYA (bank account) — the only corridor with two runtime
 * payout rails. Switching clears the other rail's recipient fields so a half-filled draft never leaks into
 * the payload the submit hook builds from `col_rail`.
 */
const ColombiaRecipient: React.FC<{ destinationCurrency: string }> = ({ destinationCurrency }) => {
  const { formData, updateField, updateRecipient, fetchQuote, lastChangedAmountField } = useDispersions();
  const { t } = useTranslations("dispersions");
  // Same normalization railForQuote applies: anything that is not exactly TRANSFIYA runs as BREB, so a stale
  // draft value can never dodge the availability guard below.
  const rail: "BREB" | "TRANSFIYA" = resolveColRail(formData.recipientFields);
  // Bre-B and Transfiya are separate corridors — either can be switched off or lose its fees on its own.
  // Step 1 keeps the country offered while ANY rail works, so this panel is where the downed one surfaces.
  // The catalog fails open: an unreadable one disables nothing.
  const corridors = useAvailableCorridors();
  const brebAvailable = corridors.isAvailable("BREB", "COL", "COP", "PAYOUT");
  const transfiyaAvailable = corridors.isAvailable("TRANSFIYA", "COL", "COP", "PAYOUT");
  const railAvailable: Record<"BREB" | "TRANSFIYA", boolean> = {
    BREB: brebAvailable,
    TRANSFIYA: transfiyaAvailable,
  };

  const switchRail = (next: "BREB" | "TRANSFIYA") => {
    if (next === rail) return;
    updateRecipient("col_rail", next);
    // The step-1 quote froze the dispersion commission for the PREVIOUS rail and
    // the backend refuses to redeem it for another corridor — re-quote with the
    // same amounts under the newly selected rail.
    const editedSide = lastChangedAmountField === "destination" ? "recv" : "send";
    const raw = editedSide === "send" ? formData.amount_origin : formData.amount_destination;
    const amount = parseAmountInputDecimal(String(raw ?? ""));
    if (amount !== null && formData.origin_currency) {
      fetchQuote({
        originCurrency: formData.origin_currency,
        destinationCountry: "COL",
        rail: next,
        ...(editedSide === "send" ? { sendAmount: amount } : { receiveAmount: amount }),
      });
    }
    // Back to the default mode, like every other field this function resets. It is not only a default on
    // Transfiya: that rail has no saved-account autofill at all, so running it in client mode would leave
    // the step-2 gate demanding a client+account selection the panel never offers, and Continue would
    // never enable.
    updateField("recipientMode", "manual");
    // Clean slate on both sides (mirrors the recipient-mode switch).
    updateField("selectedClientId", null);
    updateField("selectedClientAccountId", null);
    updateField("selectedClientAccountMethod", null);
    updateRecipient("beneficiary", "");
    updateRecipient("description", "");
    updateRecipient("breb_key_type", BrebKeyType.PHONE);
    updateRecipient("breb_key_value", "");
    updateRecipient("transfiya_account_type", TransfiyaAccountType.SAVINGS);
    updateRecipient("transfiya_account_number", "");
    updateRecipient("transfiya_bank_code", "");
    updateRecipient("transfiya_bank_name", "");
    updateRecipient("transfiya_document_type", ColombianDocumentType.CC);
    updateRecipient("transfiya_document_number", "");
  };

  // Sitting on a downed rail is a dead end: its button is disabled and Continue stays gated with nothing
  // the user can do. Auto-switch to the working rail, but ONLY while the recipient is still pristine —
  // switchRail wipes every field, and the catalog can resolve mid-typing (module memo, 2 min TTL): silently
  // erasing a half-typed Bre-B key is worse than leaving the disabled state visible.
  const selectedRailAvailable = railAvailable[rail];
  const recipientPristine =
    !formData.recipientFields?.beneficiary &&
    !formData.recipientFields?.breb_key_value &&
    !formData.recipientFields?.transfiya_account_number;
  useEffect(() => {
    if (selectedRailAvailable || !recipientPristine) return;
    const fallback = (["BREB", "TRANSFIYA"] as const).find((value) => railAvailable[value]);
    if (fallback) switchRail(fallback);
    // switchRail is recreated per render; the guards above make the effect one-shot.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRailAvailable, recipientPristine, brebAvailable, transfiyaAvailable]);

  const railButton = (value: "BREB" | "TRANSFIYA", label: string) => (
    <button
      type="button"
      onClick={() => switchRail(value)}
      disabled={!railAvailable[value]}
      className={`flex-1 rounded-lg border px-3 py-2 text-sm font-semibold transition-colors ${
        rail === value
          ? "border-primary bg-primary/10 text-primary"
          : "border-border bg-muted text-muted-foreground hover:bg-accent"
      } disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-muted`}
    >
      {label}
    </button>
  );

  const unavailableRails = (["BREB", "TRANSFIYA"] as const).filter((value) => !railAvailable[value]);

  return (
    <div className="flex flex-col gap-5">
      <div>
        <p className="mb-2 text-xs font-medium text-muted-foreground">{t("step2_col_rail_label")}</p>
        <div className="flex gap-2">
          {railButton("BREB", t("col_rail_breb"))}
          {railButton("TRANSFIYA", t("col_rail_transfiya"))}
        </div>
        {/* A switched-off rail is temporary ("try later"), a missing route/fee config is a state of the account
            ("talk to your executive") and a short float points to the other rail — see colombiaRailUnavailableKey. */}
        {unavailableRails.map((value) => (
          <p key={value} className="mt-1.5 text-xs text-muted-foreground">
            {t(value === "BREB" ? "col_rail_breb" : "col_rail_transfiya")} —{" "}
            {t(colombiaRailUnavailableKey(value, corridors.reasonFor(value, "COL", "COP", "PAYOUT")))}
          </p>
        ))}
      </div>
      {rail === "TRANSFIYA" ? (
        <ColombiaTransfiyaRecipient />
      ) : (
        <ColombiaBrebRecipient destinationCurrency={destinationCurrency} />
      )}
    </div>
  );
};

// React.memo: the panels subscribe to Redux themselves, so this only skips re-renders caused by
// page-local state (touched, modals) — its props (memoized errors, stable onBlur) keep identity.
export const Step2CountryFields: React.FC<Step2CountryFieldsProps> = React.memo(function Step2CountryFields({
  destination,
  destinationCurrency,
}) {
  if (destination === "Brasil") {
    return <BrazilPixRecipient destinationCurrency={destinationCurrency} />;
  }
  // Tolerate the unaccented API spelling — the page normalizes by ISO3, but a
  // catalog entry without a config match still arrives with the raw API name.
  if (destination === "México" || String(destination) === "Mexico") {
    return <MexicoSpeiRecipient destinationCurrency={destinationCurrency} />;
  }
  if (destination === "Argentina") {
    return <ArgentinaCbuCvuRecipient destinationCurrency={destinationCurrency} />;
  }
  if (destination === "Colombia") {
    return <ColombiaRecipient destinationCurrency={destinationCurrency} />;
  }
  // Other corridors keep the existing (PHP) recipient flow.
  return <RecipientClientSelector destination={destination} destinationCurrency={destinationCurrency} />;
});
